import { addFabricEventListener } from '../../hooks/useFabricEvent.js';
import { getCombinedRect, getNearestScrollableParent } from '@fabric-app/utils/element';
import Document from '@tiptap/extension-document';
import { PluginKey, Plugin } from '@tiptap/pm/state';
import { FbcDOMSerializer } from '../utils/FbcDOMSerializer.js';

const FbcDocumentKey = new PluginKey("fbcDocument");
const getFbcDocumentResourceId = (state) => {
  const pluginState = FbcDocumentKey.getState(state);
  if (!pluginState) return void 0;
  return pluginState.resourceId;
};
const positionOffset = 40;
const applyVisualViewportToRect = (rect, visualViewport) => {
  if (!rect) return null;
  if (!visualViewport) return rect;
  const offsetLeft = visualViewport.offsetLeft;
  const offsetTop = visualViewport.offsetTop;
  return {
    ...rect,
    left: rect.left + offsetLeft,
    top: rect.top + offsetTop,
    width: rect.width,
    height: rect.height,
    x: rect.x + offsetLeft,
    y: rect.y + offsetTop,
    right: rect.right + offsetLeft,
    bottom: rect.bottom + offsetTop
  };
};
const FbcDocument = Document.extend({
  content: "block+",
  addCommands() {
    return {
      defineResourceId: (resourceId) => ({ tr }) => {
        tr.setMeta(FbcDocumentKey, {
          resourceId
        });
        return true;
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: FbcDocumentKey,
        view(editorView) {
          let timeout = null;
          const unsub = addFabricEventListener("fabric-keyboard-event", (e) => {
            if (timeout) window.clearTimeout(timeout);
            if (!e.detail.isVisible) return;
            timeout = window.setTimeout(() => {
              const tr = editorView.state.tr.scrollIntoView();
              editorView.dispatch(tr);
            }, 100);
          });
          return {
            destroy() {
              unsub();
            }
          };
        },
        appendTransaction: (transactions, oldState, newState) => {
          let tr = null;
          if (transactions.length > 0) {
            for (const transaction of transactions) {
              if (transaction.getMeta("pointer")) {
                tr = newState.tr.scrollIntoView();
                break;
              }
            }
          }
          return tr;
        },
        state: {
          init() {
            return {
              resourceId: void 0
            };
          },
          apply(tr, value) {
            if (!tr.getMeta(FbcDocumentKey)) return value;
            const meta = tr.getMeta(FbcDocumentKey);
            return {
              ...value,
              resourceId: meta.resourceId
            };
          }
        },
        props: {
          scrollMargin: 40,
          scrollThreshold: 40,
          handleScrollToSelection: (view) => {
            const { from, to } = view.state.selection;
            const viewport = view.dom.closest("[data-radix-scroll-area-viewport]");
            if (!viewport) return false;
            const visualViewport = window.visualViewport;
            const viewportRect = applyVisualViewportToRect(viewport.getBoundingClientRect(), visualViewport);
            const combinedRect = applyVisualViewportToRect(getCombinedRect([
              view.coordsAtPos(from),
              view.coordsAtPos(to)
            ]), visualViewport);
            if (!combinedRect || !viewportRect) return false;
            const isFullyWithinViewport = combinedRect.top - positionOffset >= viewportRect.top && combinedRect.bottom + positionOffset <= viewportRect.bottom && combinedRect.left - positionOffset >= viewportRect.left && combinedRect.right + positionOffset <= viewportRect.right;
            if (isFullyWithinViewport) return true;
            const isTooBigForViewport = combinedRect.top - positionOffset < viewportRect.top || combinedRect.bottom + positionOffset > viewportRect.bottom;
            if (isTooBigForViewport) return true;
            const scrollableParent = getNearestScrollableParent(viewport);
            if (!scrollableParent) return false;
            let scrollTop = 0;
            if (combinedRect.bottom + positionOffset > viewportRect.bottom) {
              scrollTop = combinedRect.bottom + positionOffset - viewportRect.bottom;
            } else if (combinedRect.top - positionOffset < viewportRect.top) {
              scrollTop = combinedRect.top - positionOffset - viewportRect.top;
            }
            if (scrollTop !== 0) {
              scrollableParent.scrollBy({
                top: scrollTop
              });
            }
            return true;
          },
          handleDOMEvents: {
            touchend: (view) => {
              const initialSelection = view.state.selection;
              setTimeout(() => {
                if (!view.state.selection.eq(initialSelection)) {
                  const tr = view.state.tr.scrollIntoView();
                  view.dispatch(tr);
                }
              }, 100);
            }
          },
          clipboardSerializer: FbcDOMSerializer.fromSchema(this.editor.state.schema)
        }
      })
    ];
  }
});

export { FbcDocument, FbcDocumentKey, getFbcDocumentResourceId };
//# sourceMappingURL=FbcDocument.js.map
